import {
  resolveSerpentine,
  resolveSerpentineChairSides,
  SERPENTINE_MAX_SWEEP_DEG,
  SERPENTINE_MIN_SWEEP_DEG,
  type SerpentineChairSides,
} from '@peles/shared';
import type { CanvasItem } from './types';
import { serpentineSetting, type SerpentinePatch } from './helpers';

const INPUT_CLASS = 'w-full px-2 py-1 border border-gray-300 rounded text-sm';

/**
 * A serpentine's size and seating: Radius / Depth / Arc in place of a free
 * width × height, and which curve the chairs go on.
 *
 * Depth and Arc show what was typed while typing (a half-typed "1" on the way
 * to "120" must not jump to the 15° floor under the cursor) and snap to what
 * is actually drawn when the field is left.
 */
export function SerpentineControls({
  item,
  showChairSides,
  onChange,
}: {
  item: CanvasItem;
  /** Only an item that can take chairs is offered the chair picker. */
  showChairSides: boolean;
  onChange: (patch: SerpentinePatch) => void;
}) {
  const geometry = resolveSerpentine(item.widthFt, item.heightFt, item.config);
  const typedDepth = serpentineSetting(item, 'serpentineDepthFt');
  const typedSweep = serpentineSetting(item, 'serpentineSweepDeg');

  const snapToDrawn = () => {
    const patch: SerpentinePatch = {};
    if (typedDepth !== undefined && typedDepth !== geometry.depthFt)
      patch.depthFt = geometry.depthFt;
    if (typedSweep !== undefined && typedSweep !== geometry.sweepDeg)
      patch.sweepDeg = geometry.sweepDeg;
    if (Object.keys(patch).length > 0) onChange(patch);
  };

  return (
    <div className="space-y-2" data-testid="serpentine-controls">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-gray-500" htmlFor="serpentine-radius">
            Radius (ft)
          </label>
          <input
            id="serpentine-radius"
            type="number"
            value={Math.round(geometry.radiusFt * 10) / 10}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v) && v > 0) onChange({ radiusFt: Math.max(1, v) });
            }}
            min={1}
            step={0.5}
            className={INPUT_CLASS}
            data-testid="serpentine-radius-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500" htmlFor="serpentine-depth">
            Depth (ft)
          </label>
          <input
            id="serpentine-depth"
            type="number"
            value={typedDepth ?? Math.round(geometry.depthFt * 10) / 10}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v) && v > 0) onChange({ depthFt: v });
            }}
            onBlur={snapToDrawn}
            min={0.5}
            step={0.5}
            className={INPUT_CLASS}
            data-testid="serpentine-depth-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500" htmlFor="serpentine-sweep">
            Arc (°)
          </label>
          <input
            id="serpentine-sweep"
            type="number"
            value={typedSweep ?? Math.round(geometry.sweepDeg)}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange({ sweepDeg: v });
            }}
            onBlur={snapToDrawn}
            min={SERPENTINE_MIN_SWEEP_DEG}
            max={SERPENTINE_MAX_SWEEP_DEG}
            step={15}
            className={INPUT_CLASS}
            data-testid="serpentine-sweep-input"
          />
        </div>
      </div>
      {showChairSides && (
        <div>
          <label className="block text-xs text-gray-500" htmlFor="serpentine-chair-sides">
            Chair Sides
          </label>
          <select
            id="serpentine-chair-sides"
            value={resolveSerpentineChairSides(item.config)}
            onChange={(e) => onChange({ chairSides: e.target.value as SerpentineChairSides })}
            className={INPUT_CLASS}
            data-testid="serpentine-chair-sides-select"
          >
            <option value="outer">Outer curve</option>
            <option value="inner">Inner curve</option>
            <option value="both">Both curves</option>
          </select>
        </div>
      )}
    </div>
  );
}
